import { ClockIcon } from "@/pages/taskpane/components/icons/IconCustom";
import HStack from "@/pages/taskpane/components/layout/HStack";
import Accordion from "@/pages/taskpane/components/ui/Accordion";
import TitleSetting from "@/pages/taskpane/components/ui/TitleSetting";
import { settingLabels } from "@/pages/taskpane/shared/constants/setting";
import { useThemeStore, useSettingStore, useOutlookStore, useAppConfigStore } from "@/pages/taskpane/shared/stores";
import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Select from "@/pages/taskpane/components/ui/Select";
import Button from "@/pages/taskpane/components/ui/Button";
import Divider from "@/pages/taskpane/components/ui/Divider";
import {
  dateFormatOptions,
  timeFormatOptions,
  dateSeparatorOptions,
  firstDayOfWeekOptions,
} from "@/pages/taskpane/shared/constants/appConstants";
import VStack from "@/pages/taskpane/components/layout/VStack";
import { OptionType } from "@/pages/taskpane/shared/types";
import { DateTimeFormatEntity, OrganizerSettingEntity } from "../services/entities/organizerSettingEntity";
import { useCreateOrganizerSetting } from "../services/useCreateOrganizerSetting";
import { EDateSeparator } from "../services/entities/enum";

const DateTimeSetting = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useThemeStore();
  const [isOpen, setIsOpen] = useState(false);

  const header = (
    <HStack justifyContent="between" className="w-full" onClick={() => setIsOpen(!isOpen)}>
      <TitleSetting icon={<ClockIcon isDarkMode={isDarkMode} />} label={t(settingLabels.dateTime)} />
    </HStack>
  );

  return (
    <Accordion header={header} isOpen={isOpen} onToggleOpen={() => setIsOpen(!isOpen)}>
      {isOpen ? <DateTimeContent /> : null}
    </Accordion>
  );
};

export default DateTimeSetting;

const DateTimeContent = () => {
  const { t } = useTranslation();
  // const showToast = useToastStore((state) => state.showToast);
  const { organizer } = useOutlookStore();
  const { firstDayOfWeek, dateFormat, timeFormat, dateSeparator, updateDateTimeFormat } = useSettingStore();
  const { parameters } = useAppConfigStore();

  const [selectedFirstDayOfWeek, setSelectedFirstDayOfWeek] = useState(firstDayOfWeek);
  const [selectedDateFormat, setSelectedDateFormat] = useState(dateFormat);
  const [selectedTimeFormat, setSelectedTimeFormat] = useState(timeFormat);
  const [selectedDateSeparator, setSelectedDateSeparator] = useState(dateSeparator);

  const { mutate: updateDateTimeFormatMutation, isPending: isUpdatingDateTimeFormat } = useCreateOrganizerSetting({
    mutationConfig: {
      onSuccess: (data: OrganizerSettingEntity) => {
        updateDateTimeFormat(new DateTimeFormatEntity({
          ...data.dateTimeFormat,
          dateSeparator: data?.dateTimeFormat?.dateSeparator || parameters?.dateSeperator || EDateSeparator.Default,
        }));
        // showToast(t("Setting updated successfully"), true);
      },
      onError: () => {
        // showToast(t("Failed to update setting"), false);
      },
    },
  });

  const handleApply = useCallback(() => {
    updateDateTimeFormatMutation({
      organizerEmail: organizer,
      dateTimeFormat: {
        firstDayOfWeek: selectedFirstDayOfWeek,
        dateFormat: selectedDateFormat,
        timeFormat: selectedTimeFormat,
        dateSeparator: selectedDateSeparator === EDateSeparator.Default ? "" : selectedDateSeparator as EDateSeparator,
      },
    });
  }, [
    selectedDateFormat,
    selectedTimeFormat,
    selectedDateSeparator,
    selectedFirstDayOfWeek,
    organizer,
    updateDateTimeFormatMutation,
  ]);

  return (
    <div className="w-full">
      <Divider className="my-3" />
      <VStack gap={4}>
        {/* First day of the week */}
        <FormSettingCommon
          label={t("First day of the week")}
          options={firstDayOfWeekOptions}
          value={selectedFirstDayOfWeek}
          placeholder={t("Select first day of the week")}
          onChange={(value) => setSelectedFirstDayOfWeek(value as number)}
        />

        {/* Time Format */}
        <FormSettingCommon
          label={t("Time Format")}
          options={timeFormatOptions}
          value={selectedTimeFormat}
          placeholder={t("Select time format")}
          onChange={(value) => setSelectedTimeFormat(value as string)}
        />

        {/* Date Format */}
        <FormSettingCommon
          label={t("Date Format")}
          options={dateFormatOptions}
          value={selectedDateFormat}
          placeholder={t("Select date format")}
          onChange={(value) => setSelectedDateFormat(value as string)}
        />

        {/* Date Separator */}
        <FormSettingCommon
          label={t("Date Separator")}
          options={dateSeparatorOptions}
          value={selectedDateSeparator}
          placeholder={t("Select date separator")}
          onChange={(value) => setSelectedDateSeparator(value as string)}
        />
      </VStack>
      <Divider className="my-3" />
      <HStack justifyContent="end" className="w-full">
        <Button
          isDefault={true}
          label={t("Apply")}
          className="w-[50%] py-2 text-xs"
          disabled={isUpdatingDateTimeFormat}
          onClick={handleApply}
        />
      </HStack>
    </div>
  );
};

type FormSettingCommonProps = {
  label: string;
  value: string | number | boolean;
  options: OptionType[];
  placeholder: string;
  onChange: (value: string | number | boolean) => void;
};

const FormSettingCommon = ({ label, options, value, placeholder, onChange }: FormSettingCommonProps) => {
  return (
    <div className="w-full">
      <p className="text-sm font-semibold mb-[6px]">{label}</p>
      <Select
        placeholder={placeholder}
        className="w-full h-8 text-sm"
        options={options}
        value={value}
        onChange={onChange}
      />
    </div>
  );
};
